import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, TrendingUp, Eye, Trophy, Share2, Sparkles, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminViralTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/viral/admin/stats`);
      setData(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="admin-viral-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  if (!data) return <p className="text-slate-500 text-sm">Unable to load viral stats.</p>;

  const referralRate = data.total_users
    ? Math.round((data.referred_users / data.total_users) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-viral-tab">
      <div className="mb-6">
        <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium flex items-center gap-2">
          <Sparkles className="h-3 w-3" /> Distribution Network
        </span>
        <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Viral Referral Analytics</h2>
        <p className="text-sm text-slate-500 mt-1">Track user-to-user invites, conversion funnel, and viral coefficient.</p>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricBox icon={<Users className="h-4 w-4" />} label="Total Users" value={data.total_users} testid="metric-total-users" />
        <MetricBox icon={<Share2 className="h-4 w-4" />} label="Invited In" value={data.referred_users} testid="metric-referred" highlight />
        <MetricBox icon={<Trophy className="h-4 w-4" />} label="Active Referrers" value={data.active_referrers} testid="metric-active-referrers" />
        <MetricBox icon={<Eye className="h-4 w-4" />} label="Link Clicks" value={data.total_clicks} testid="metric-clicks" />
        <MetricBox icon={<TrendingUp className="h-4 w-4" />} label="K-Factor" value={data.viral_coefficient} testid="metric-k-factor" highlight />
      </div>

      {/* Funnel */}
      <div className="luxury-card p-6" data-testid="viral-funnel">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Conversion Funnel (Last 7 Days)</h3>
        <div className="space-y-3">
          <FunnelBar label="Link Clicks" value={data.total_clicks} max={Math.max(data.total_clicks, 1)} color="bg-slate-200" testid="funnel-clicks" />
          <FunnelBar label="Signups via Referral" value={data.recent_signups_7d} max={Math.max(data.total_clicks, 1)} color="bg-[#E07A3A]" testid="funnel-signups" />
          <FunnelBar label="Referred Users (Total)" value={data.referred_users} max={Math.max(data.total_users, 1)} color="bg-green-600" testid="funnel-referred-total" />
        </div>
        <p className="text-xs text-slate-400 mt-4">
          {referralRate}% of all users joined via a referral link. Viral coefficient (K) = {data.viral_coefficient}.
          {data.viral_coefficient >= 1 ? ' Exponential growth zone.' : data.viral_coefficient >= 0.5 ? ' Healthy growth.' : ' Grow referrals to increase virality.'}
        </p>
      </div>

      {/* Top referrers */}
      <div className="luxury-card p-6" data-testid="admin-top-referrers">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Top Referrers</h3>
        {data.top_referrers.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No active referrers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[10px] tracking-wider uppercase text-slate-400">
                  <th className="py-2">Rank</th>
                  <th className="py-2">User</th>
                  <th className="py-2">Role</th>
                  <th className="py-2">Code</th>
                  <th className="py-2 text-right">Signups</th>
                  <th className="py-2 text-right">Qualified</th>
                </tr>
              </thead>
              <tbody>
                {data.top_referrers.map((u, idx) => (
                  <tr key={u.email} className="border-b border-slate-100 last:border-0">
                    <td className="py-3 font-['Playfair_Display'] text-lg font-bold text-[#E07A3A]">#{idx + 1}</td>
                    <td className="py-3">
                      <p className="text-[#1A1A1A] font-medium">{u.name}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </td>
                    <td className="py-3 text-xs uppercase tracking-wider text-slate-500">{u.role}</td>
                    <td className="py-3 font-mono text-xs text-[#E07A3A]">{u.code}</td>
                    <td className="py-3 text-right font-semibold">{u.referrals_count}</td>
                    <td className="py-3 text-right text-green-600 font-semibold">{u.successful_referrals_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reward tiers reference */}
      <div className="luxury-card p-6" data-testid="admin-reward-tiers">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Active Reward Tiers</h3>
        <div className="grid sm:grid-cols-5 gap-3">
          {data.reward_tiers.map(tier => (
            <div key={tier.key} className="bg-slate-50 border border-slate-200 p-3 text-center">
              <p className="text-2xl font-['Playfair_Display'] text-[#E07A3A] font-bold">{tier.count}×</p>
              <p className="text-[11px] text-[#1A1A1A] mt-1 leading-tight">{tier.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetricBox = ({ icon, label, value, testid, highlight }) => (
  <div className={`luxury-card p-4 ${highlight ? 'border-[#E07A3A]/30' : ''}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-[10px] tracking-[0.2em] uppercase font-bold mb-1">
      {icon}
      {label}
    </div>
    <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#E07A3A]">{value}</p>
  </div>
);

const FunnelBar = ({ label, value, max, color, testid }) => {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-[#1A1A1A]">{value}</span>
      </div>
      <div className="w-full bg-slate-100 h-2 overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default AdminViralTab;
